import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const VN_TIMEZONE = 'Asia/Ho_Chi_Minh';

function hasExplicitTimezone(raw: string): boolean {
  if (raw.endsWith('Z')) return true;
  return /[+-]\d{2}:?\d{2}$/.test(raw);
}

/** Parse API timestamps as UTC instants, then expose Vietnam wall clock. */
export function parseApiInstant(
  value: string | Date | number | Dayjs | null | undefined
): Dayjs | null {
  if (value == null || value === '') return null;
  if (dayjs.isDayjs(value)) return value.utc().tz(VN_TIMEZONE);
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return dayjs.utc(value.toISOString()).tz(VN_TIMEZONE);
  }
  if (typeof value === 'number') {
    const d = dayjs.utc(value);
    return d.isValid() ? d.tz(VN_TIMEZONE) : null;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return dayjs.tz(raw, VN_TIMEZONE);
  }
  if (hasExplicitTimezone(raw)) {
    const d = dayjs(raw);
    return d.isValid() ? d.tz(VN_TIMEZONE) : null;
  }
  const d = dayjs.utc(raw);
  return d.isValid() ? d.tz(VN_TIMEZONE) : null;
}

export function formatVnDateTime(
  value: string | Date | number | Dayjs | null | undefined,
  pattern = 'DD/MM/YYYY HH:mm'
): string {
  const d = parseApiInstant(value);
  return d ? d.format(pattern) : '—';
}

export function formatVnDateTimeSeconds(
  value: string | Date | number | Dayjs | null | undefined
): string {
  return formatVnDateTime(value, 'DD/MM/YYYY HH:mm:ss');
}

export function formatVnDate(
  value: string | Date | number | Dayjs | null | undefined,
  pattern = 'DD/MM/YYYY'
): string {
  const d = parseApiInstant(value);
  return d ? d.format(pattern) : '—';
}

export function formatVnTime(
  value: string | Date | number | Dayjs | null | undefined,
  pattern = 'HH:mm'
): string {
  const d = parseApiInstant(value);
  return d ? d.format(pattern) : '—';
}

/** Short date+time for tables: dd/MM HH:mm */
export function formatVnDateTimeShort(
  value: string | Date | number | Dayjs | null | undefined
): string {
  return formatVnDateTime(value, 'DD/MM HH:mm');
}

/** Calendar date-only strings (YYYY-MM-DD) without UTC day shift. */
export function formatVnCalendarDate(
  value: string | Date | null | undefined,
  pattern = 'DD/MM/YYYY'
): string {
  if (!value) return '—';
  const raw = typeof value === 'string' ? value.trim() : '';
  if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return dayjs.tz(raw, VN_TIMEZONE).format(pattern);
  }
  return formatVnDate(value, pattern);
}

/** Chart axis: DD/MM from date or instant. */
export function formatVnChartDay(
  value: string | Date | null | undefined
): string {
  return formatVnCalendarDate(value, 'DD/MM');
}

export function formatRelativeVn(
  value: string | Date | number | Dayjs | null | undefined
): string {
  const d = parseApiInstant(value);
  if (!d) return '—';
  const now = dayjs().tz(VN_TIMEZONE);
  const diffMin = now.diff(d, 'minute');
  if (diffMin < 1) return 'Vừa xong';
  if (diffMin < 60) return `${diffMin} phút trước`;
  const diffHr = now.diff(d, 'hour');
  if (diffHr < 24) return `${diffHr} giờ trước`;
  const diffDay = now.diff(d, 'day');
  if (diffDay === 1) return 'Hôm qua';
  if (diffDay < 7) return `${diffDay} ngày trước`;
  return formatVnDate(d);
}

/** English-style long format but always Vietnam timezone. */
export function formatVnDateTimeEn(
  value: string | Date | number | Dayjs | null | undefined
): string {
  const d = parseApiInstant(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: VN_TIMEZONE,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d.toDate());
}

/** dayjs instance pinned to Vietnam for date-range pickers. */
export function vnDayjs(value?: string | Date | number | Dayjs | null): Dayjs {
  if (value == null || value === '') return dayjs().tz(VN_TIMEZONE);
  return parseApiInstant(value) ?? dayjs().tz(VN_TIMEZONE);
}
